/**
 * Every German string the user sees, in one place.
 *
 * Code, API fields and logs stay English; only this file is German. That keeps the
 * codebase workable for any developer while the team sees their own language, and it is
 * the single file to touch if a second locale is ever added.
 */

export const de = {
  app: {
    title: 'Paketverfolgung',
    subtitle: 'Sendungen aus dem gemeinsamen Postfach',
  },
  auth: {
    title: 'Anmeldung erforderlich',
    intro: 'Melde dich mit deinem GitLab-Konto an, um die Paketverfolgung zu sehen.',
    signIn: 'Mit GitLab anmelden',
    signOut: 'Abmelden',
    checking: 'Anmeldung wird geprüft …',
    notConfigured:
      'Die Anmeldung ist aktiviert, aber noch nicht vollständig eingerichtet. Bitte GITLAB_URL, GITLAB_CLIENT_ID, GITLAB_CLIENT_SECRET und AUTH_SESSION_SECRET setzen.',
    unreachable: 'Der Anmeldedienst ist nicht erreichbar.',
    retry: 'Erneut versuchen',
    admin: 'Administrator',
  },
  nav: {
    dashboard: 'Übersicht',
    projects: 'Projekte',
    status: 'Systemstatus',
    menu: 'Menü',
    help: 'Hilfe',
  },
  today: {
    title: 'Heute',
    hint: 'In Zustellung, zur Abholung bereit oder mit Problem',
  },
  dayGroups: {
    attention: 'Abholen oder klären',
    overdue: 'Überfällig',
    today: 'Heute',
    tomorrow: 'Morgen',
    week: 'Diese Woche',
    later: 'Später',
    none: 'Ohne Termin',
  },
  sections: {
    favorites: 'Favoriten',
    favoritesHint: 'Deine markierten Sendungen – auch in ihrem eigenen Bereich zu finden',
    active: 'Unterwegs',
    activeHint: 'Sendungen, die gerade laufen oder Aufmerksamkeit brauchen',
    delivered: 'Zugestellt',
    /** Filled in with the configured window, e.g. "Zugestellt in den letzten 14 Tagen". */
    deliveredHint: (days: number) => `Zugestellt in den letzten ${days} Tagen`,
    announced: 'Angekündigt',
    announcedHint: 'Angekündigt, aber noch nicht beim Dienstleister',
    archived: 'Archiviert',
    archivedHint: 'Ältere Zustellungen und manuell archivierte Sendungen',
  },
  status: {
    UNKNOWN: 'Unbekannt',
    ANNOUNCED: 'Angekündigt',
    IN_TRANSIT: 'Unterwegs',
    OUT_FOR_DELIVERY: 'In Zustellung',
    PICKUP_READY: 'Zur Abholung',
    EXCEPTION: 'Problem',
    DELIVERED: 'Zugestellt',
  },
  /** The route's steps, short enough to sit under four dots in the drawer. */
  route: {
    steps: ['Angekündigt', 'Unterwegs', 'Zustellung', 'Zugestellt'],
    done: 'erledigt',
    open: 'offen',
  },
  confidence: {
    certain: 'Dienstleister bestätigt',
    probable: 'Dienstleister anhand der Nummer erkannt',
    guessed: 'Dienstleister geraten – bitte prüfen',
  },
  parcel: {
    trackingNumber: 'Sendungsnummer',
    carrier: 'Dienstleister',
    description: 'Inhalt',
    sender: 'Absender',
    recipient: 'Empfänger',
    recipientAddress: 'Empfängeradresse',
    onlyCity: 'nur Ort',
    onlyCityHint: 'Nur der Ort ist bekannt (aus der Dienstleister-API). Vollständige Anschrift hier eintragen.',
    addressFromCarrierOnlyCity: 'Vom Dienstleister ist nur der Ort bekannt.',
    orderNumber: 'Bestellnummer',
    project: 'Projekt',
    notes: 'Notizen',
    expected: 'Zustellung',
    delivered: 'Zugestellt am',
    refreshed: 'Aktualisiert',
    pickupLocation: 'Abholort',
    hidden: 'Versteckt',
    isReturn: 'Rücksendung',
    returnOf: 'Rücksendung zu',
    returns: 'Zurückgeschickt',
    markAsReturn: 'Das ist eine Rücksendung (wir schicken zurück)',
    hiddenBadge: 'Nur für Admins sichtbar',
    hiddenBy: (who: string) => `Versteckt von ${who}`,
    hideHint:
      'Versteckt die Sendung für alle außer Administratoren. Sie wird weiter verfolgt und '
      + 'abgefragt.',
    unhideHint: 'Diese Sendung sehen zurzeit nur Administratoren.',
    deleteHint:
      'Löschen entfernt die Sendung samt Verlauf und sperrt ihre Nummer. Wer sie nur vom '
      + 'Brett nehmen will, archiviert sie besser.',
    timeline: 'Verlauf',
    sectionEdit: 'Angaben bearbeiten',
    sectionActions: 'Aktionen',
    sectionAdmin: 'Verwaltung',
    source: 'Quelle',
    statusSource: 'Status laut',
    statusSourceApi: 'Dienstleister (API)',
    statusSourceEmail: 'E-Mail',
    statusSourceManual: 'manueller Eintrag',
    statusSourceApiHint:
      'Sobald der Dienstleister selbst gemeldet hat, bestimmt nur noch die API den Status. E-Mails bleiben im Verlauf sichtbar.',
    noProject: 'Kein Projekt',
    noDescription: 'Ohne Beschreibung',
    fallbackTitle: (from: string) => `Sendung von ${from}`,
    overdue: (days: number) => (days === 1 ? 'seit gestern überfällig' : `seit ${days} Tagen überfällig`),
    /** Under the "Überfällig" heading, which already says it. */
    overdueShort: (days: number) => (days === 1 ? 'seit gestern' : `seit ${days} Tagen`),
    deliveredOn: 'Zugestellt',
    openAtCarrier: 'Beim Dienstleister öffnen',
    openAtAmazon: 'Bestellung bei Amazon öffnen',
    orderNumberFixed: 'Bestimmt die Sendung bei Amazon – nicht änderbar.',
    copyNumber: 'Nummer kopieren',
    copied: 'Kopiert',
    noEvents: 'Noch keine Meldungen.',
    staleDays: (days: number) => `seit ${days} Tagen keine Meldung`,
    staleHint: 'Dieser Dienstleister hat keine API; der Status kommt nur aus E-Mails.',
    markDeliveredHint:
      'Für Dienstleister ohne Schnittstelle: Der Status kommt danach von Hand und wird von E-Mails nicht mehr überschrieben.',
    archiveHint: 'Verschiebt die Sendung nach „Archiviert“. Der Verlauf bleibt erhalten.',
    archivedHint: 'Diese Sendung liegt im Archiv.',
  },
  actions: {
    add: 'Sendung hinzufügen',
    refresh: 'Aktualisieren',
    refreshNow: 'Jetzt abfragen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    archive: 'Archivieren',
    hide: 'Verstecken',
    unhide: 'Wieder für alle sichtbar',
    deleteParcel: 'Sendung löschen',
    confirmDeleteParcel:
      'Sendung mit ihrem Verlauf endgültig löschen? Die Sendungsnummer wird gesperrt, damit '
      + 'spätere E-Mails sie nicht erneut anlegen. Im Systemstatus lässt sich die Sperre aufheben.',
    unblock: 'Sperre aufheben',
    favorite: 'Zu Favoriten hinzufügen',
    unfavorite: 'Aus Favoriten entfernen',
    sendBack: 'Zurückschicken',
    markDelivered: 'Als zugestellt markieren',
    confirmDelivered:
      'Sendung als zugestellt markieren? Der Status kommt dann von Hand, nicht mehr aus E-Mails.',
    unarchive: 'Wieder aktiv setzen',
    details: 'Details',
    close: 'Schließen',
    search: 'Suchen…',
    syncEmails: 'Postfach abrufen',
    syncCarriers: 'Dienstleister abfragen',
    reparse: 'Erneut auswerten',
  },
  push: {
    on: 'Benachrichtigungen an',
    off: 'Benachrichtigungen einschalten',
    blocked: 'Im Browser blockiert',
    blockedHint:
      'Dieser Browser blockiert Benachrichtigungen. In den Website-Einstellungen des '
      + 'Browsers wieder erlauben.',
    hint:
      'Meldet Änderungen an deinen Favoriten: in Zustellung, zur Abholung, Problem, '
      + 'zugestellt. Gilt pro Browser – auf dem Handy einmal dort einschalten.',
  },
  filters: {
    allDirections: 'Ein- und ausgehend',
    inboundOnly: 'Nur eingehend',
    returnsOnly: 'Nur Rücksendungen',
    allProjects: 'Alle Projekte',
    noProjectOnly: 'Ohne Projekt',
    allCarriers: 'Alle Dienstleister',
    toggle: 'Filter',
    reset: 'Filter zurücksetzen',
  },
  add: {
    title: 'Sendung hinzufügen',
    numberLabel: 'Sendungsnummer',
    numberHint: 'Der Dienstleister wird automatisch erkannt, falls nicht angegeben.',
    carrierAuto: 'Automatisch erkennen',
    submit: 'Anlegen',
    returnTitle: 'Rücksendung anlegen',
    returnOf: (label: string) => `zu: ${label}`,
    returnSubmit: 'Rücksendung anlegen',
    returnNumberLabel: 'Sendungsnummer vom Retourenlabel',
    returnNumberHint:
      'Die Nummer steht auf dem Rücksendeetikett – nicht die Nummer der ursprünglichen Sendung.',
    returnNotesLabel: 'Grund / Notiz',
    returnNotesPlaceholder: 'z. B. falsche Größe, Gutschrift erwartet',
    isReturn: 'Rücksendung – wir schicken dieses Paket zurück',
    isReturnHint:
      'Retourentexte des Dienstleisters („Retoure zugestellt“) gelten dann als normaler '
      + 'Verlauf, nicht als Problem.',
  },
  projects: {
    title: 'Projekte',
    name: 'Name',
    customer: 'Kunde',
    description: 'Beschreibung',
    parcelCount: 'Sendungen',
    active: 'Aktiv',
    inactive: 'Inaktiv',
    showInactive: 'Inaktive anzeigen',
    create: 'Projekt anlegen',
    empty: 'Noch keine Projekte angelegt.',
    deactivated: 'Projekt wurde deaktiviert, weil ihm noch Sendungen zugeordnet sind.',
    confirmDelete: 'Projekt wirklich entfernen?',
  },
  sync: {
    title: 'Systemstatus',
    imap: 'Postfach',
    imapConfigured: 'Konfiguriert',
    imapMissing: 'Nicht konfiguriert – Sendungen können nur manuell angelegt werden',
    mailCheck: 'Verbindung prüfen',
    scheduler: 'Hintergrundaufgaben',
    pollWindow: 'Abfragezeitfenster',
    inWindow: 'aktiv',
    outOfWindow: 'außerhalb (06–22 Uhr)',
    lastImap: 'Letzter Postfachabruf',
    lastPoll: 'Letzte Dienstleister-Abfrage',
    unparsed: 'Nicht erkannte E-Mails',
    unparsedHint:
      'Diese E-Mails enthielten keine erkennbare Sendungsnummer oder kamen von einem nicht bestätigten Absender. Sie sind die Grundlage, um die Auswertung zu verbessern.',
    /** Warum eine E-Mail nicht ausgewertet wurde, z. B. "Absender nicht bestätigt (DMARC: fail)". */
    unparsedReason: 'Grund',
    /** Tooltip und Hinweis bei einer E-Mail, deren Inhalt nach der Aufbewahrungsfrist gelöscht wurde. */
    contentPurged: (date: string) =>
      `Inhalt am ${date} nach Ablauf der Aufbewahrungsfrist gelöscht – erneutes Auswerten nicht möglich`,
    contentPurgedShort: 'Inhalt gelöscht',
    blockedNumbers: 'Gesperrte Sendungsnummern',
    blockedHint:
      'Nummern gelöschter Sendungen. E-Mails, die sie nennen, legen keine Sendung mehr an und '
      + 'stehen oben unter „Nicht erkannte E-Mails“. Nach dem Aufheben legt die nächste E-Mail '
      + 'die Sendung wieder an.',
    blockedEmpty: 'Keine gesperrten Nummern.',
    blockedBy: (who: string, when: string) => `gesperrt von ${who} am ${when}`,
    carriers: 'Dienstleister-Schnittstellen',
    configured: 'konfiguriert',
    notConfigured: 'kein Zugang',
    budget: 'Tageskontingent',
    /**
     * "200/200" was read as "200 used of 200" -- the exact opposite of what it shows.
     * Spelling out "noch" removes the ambiguity without needing a legend.
     */
    budgetLeft: (remaining: number, total: number) => `noch ${remaining} von ${total} Abfragen`,
    budgetHint: 'Verbrauchte Abfragen werden um Mitternacht zurückgesetzt.',
    never: 'noch nie',
    apiHint:
      'Ohne API-Zugang wird der Status ausschließlich aus E-Mails abgeleitet. Das funktioniert, ist aber nur so aktuell wie die letzte E-Mail.',
  },
  errors: {
    generic: 'Etwas ist schiefgelaufen.',
    loading: 'Daten konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
  },
  alerts: {
    heading: 'Störung',
    details: 'Details',
    /** e.g. "Bitte Alex informieren: alex@mintech.de" -- omitted entirely when the API sends no contact. */
    /**
     * ADMIN_CONTACT is written as "Alex <alex@mintech.de>"; read as a sentence that is
     * "Bitte Alex informieren: alex@mintech.de", not the raw address-book form.
     */
    contact: (raw: string) => {
      const match = raw.match(/^\s*([^<]+?)\s*<([^>]+)>\s*$/)
      return match ? `Bitte ${match[1]} informieren: ${match[2]}` : `Bitte ${raw} informieren`
    },
    dismiss: 'Hinweis ausblenden',
    openStatus: 'Systemstatus öffnen',
  },
  /**
   * The first-login tutorial. Six steps, shown once per person (the server remembers), and
   * re-openable from the header -- so it may be read at leisure rather than skimmed once.
   */
  tutorial: {
    open: 'Tutorial öffnen',
    skip: 'Überspringen',
    back: 'Zurück',
    next: 'Weiter',
    done: 'Los geht’s',
    progress: (current: number, total: number) => `Schritt ${current} von ${total}`,
    goTo: (step: number) => `Zu Schritt ${step}`,
    steps: [
      {
        title: 'Willkommen zur Paketverfolgung',
        body: 'Hier siehst du alle Sendungen der Firma an einem Ort – automatisch aus dem gemeinsamen Postfach gelesen und, wo es eine Schnittstelle gibt, direkt beim Dienstleister aktualisiert. Die Übersicht ist von oben nach unten in vier Abschnitte geteilt.',
        points: [
          '„Angekündigt“: bestellt, aber noch nicht unterwegs.',
          '„Unterwegs“: nach Dringlichkeit – Probleme zuerst, dann heutige Zustellungen, dann Abholungen.',
          '„Zugestellt“: die letzten 14 Tage.',
          '„Archiviert“: älter oder von Hand archiviert, eingeklappt.',
        ],
      },
      {
        title: 'Woher die Sendungen kommen',
        body: 'Alles, was an parcels@mintech.de geht, wird ausgewertet: Mails von DHL, UPS, FedEx, DPD, GLS und Hermes ebenso wie Versandbestätigungen von Shops. Bekommst du eine Versandbestätigung selbst? Leite sie einfach an das Postfach weiter – auch Amazon-Bestellbestätigungen, daraus entsteht die Sendung schon vor dem Versand.',
        points: [
          'Tipp: Bei Bestellungen parcels@mintech.de als Benachrichtigungsadresse angeben, dann kommen auch GLS- und DPD-Meldungen an.',
        ],
      },
      {
        title: 'Eine Zeile lesen',
        body: 'Jede Zeile fasst eine Sendung zusammen: Status-Pille (Unterwegs, In Zustellung, Zur Abholung, Problem, Zugestellt), Dienstleister-Badge und Sendungsnummer – bei Amazon die Bestellnummer, ein Klick kopiert sie. Danach folgen Beschreibung, Empfängeradresse (kompakt; „nur Ort“, wenn der Dienstleister nur die Stadt nennt), Projekt-Chip und die letzte Meldung.',
        points: [
          '„Seit N Tagen keine Meldung“ in Bernstein heißt: Dienstleister ohne Schnittstelle und lange nichts gehört – nachsehen lohnt sich.',
        ],
      },
      {
        title: 'Das Detailfenster',
        body: 'Ein Klick auf eine Zeile zeigt den Verlauf aller Meldungen mit Quelle (Dienstleister-API, E-Mail, manuell), dazu den Link zum Dienstleister und „Jetzt abfragen“ – Letzteres nur bei Dienstleistern mit Schnittstelle. Beschreibung, Projekt, Empfängeradresse und Notizen bearbeitest du dort; Änderungen von Hand werden nie von späteren Mails überschrieben.',
        points: [
          '„Als zugestellt markieren“, wenn das Paket da ist, aber keine Meldung kam.',
          '„Archivieren“ statt Löschen; Projekte löschen können nur Administratoren.',
        ],
      },
      {
        title: 'Suchen, filtern, anlegen',
        body: 'Das Suchfeld findet Sendungen über Nummer, Beschreibung oder Absender, daneben filterst du nach Projekt und Dienstleister. „Sendung hinzufügen“ ist für Sendungen, zu denen keine Mail kam – die Nummer genügt, der Dienstleister wird erkannt. Projekte verwaltest du unter „Projekte“.',
      },
      {
        title: 'Wenn etwas nicht läuft',
        body: 'Ein Banner oben meldet Störungen (Postfach, Dienstleister-API, Kontingent, Sicherung) und nennt, wen du informieren sollst. Wegklicken gilt bis zum nächsten Seitenaufruf. Unter „Systemstatus“ sehen Administratoren die Details.',
        points: ['Dieses Tutorial findest du jederzeit über das ? oben rechts.'],
      },
    ],
  },
  time: {
    today: 'Heute',
    yesterday: 'Gestern',
    tomorrow: 'Morgen',
  },
} as const
